/* eslint-disable jsx-a11y/anchor-is-valid */

import LogicContext from '../../Context/LogicContext';
import { useEffect, useRef } from 'react';
import { useState } from 'react';
import { useContext } from 'react';
import { SetRecoilCorporateApprovedList, GetRecoilCorporateApprovedList } from '../../store/corporateApproveList';
import { IoMdRefresh } from 'react-icons/io';
import { IoCloudDownloadOutline } from 'react-icons/io5';
import Tooltip from '@mui/material/Tooltip';
import { useLocation, useNavigate } from 'react-router-dom';
import { corporateService } from '../../services/corporation.service';
import CoApproveFilter from './CoApproveFilter';
import GeneralCustomTable from '../../helpers/GeneralCustomTable';
import moment from 'moment';
import Papa from 'papaparse'; // ← Added for reliable CSV export

const columns = [
  {
    title: 'ID',
    key: 'Corporate_Id',
    dataIndex: 'Corporate_Id',
    className: 'text-primary',
    sorter: (a, b) => (a?.Corporate_Id ?? '') - (b?.Corporate_Id ?? ''),
  },
  {
    title: 'Corporate Name',
    dataIndex: 'Corporate_Name',
    className: 'text-primary font-bold',
    sorter: (a, b) => (a?.Corporate_Name ?? '').localeCompare(b?.Corporate_Name ?? ''),
  },
  {
    title: 'Mobile',
    dataIndex: 'Corporate_Contact_Person_Mobile',
    sorter: (a, b) => (a?.Corporate_Contact_Person_Mobile ?? '').localeCompare(b?.Corporate_Contact_Person_Mobile ?? ''),
  },
  {
    title: 'Email',
    dataIndex: 'Corporate_Email_Address',
    sorter: (a, b) => (a?.Corporate_Email_Address ?? '').localeCompare(b?.Corporate_Email_Address ?? ''),
  },
  {
    title: 'Contact Person',
    dataIndex: 'Corporate_Contact_Person',
    sorter: (a, b) => (a?.Corporate_Contact_Person ?? '').localeCompare(b?.Corporate_Contact_Person ?? ''),
  },
  {
    title: 'Number of officer',
    dataIndex: 'Number_Of_Officer_Name',
    sorter: (a, b) => (a?.Number_Of_Officer_Name ?? '').localeCompare(b?.Number_Of_Officer_Name ?? ''),
  },
  {
    title: 'Status',
    dataIndex: 'Corporate_Approval_Status_Name',
    sorter: (a, b) => (a?.Corporate_Approval_Status_Name ?? '').localeCompare(b?.Corporate_Approval_Status_Name ?? ''),
    render: record => (
      <span className={`${record === 'Approved' ? 'font-bold text-green-500' : record === 'Inactive' ? 'text-red-500' : ''}`}>
        {record}
      </span>
    ),
  },
];

function CorporateApprovalPage() {
  const logicContext = useContext(LogicContext);
  const getRecoilList = GetRecoilCorporateApprovedList();
  const setRecoilList = SetRecoilCorporateApprovedList();

  const [tableList, setTableList] = useState([]);

  const navigate = useNavigate();
  const isMounted = useRef(false);

  const { pathname } = useLocation();
  const url = pathname.split('/');
  const currModuleId = url[1];
  const currModuleName = url[2];
  const currUserMenu = url[3];

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const new_list = queryParams.get('new_list');

  useEffect(() => {
    isMounted.current = true;

    if (getRecoilList.Corporate_Info?.length) {
      setTableList(getRecoilList.Corporate_Info);

      if (new_list) {
        onStatusFilterSelect({
          Status_Id: 1,
          Status_Name: '1st App. Pending',
          label: '1st App. Pending',
        });
      }
    }

    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getRecoilList]);

  function getCorpListList() {
    corporateService.getCorporateApprovedList().then(response => {
      if (isMounted.current && response?.data?.Corporate_Info) {
        setRecoilList(response?.data);
        setTableList(response?.data?.Corporate_Info);
      }
    });
  }

  function onStatusFilterSelect(filter) {
    const option = filter.Status_Id;
    if (option == '0' || !option) {
      setTableList(getRecoilList.Corporate_Info || []);
    } else {
      const newList = getRecoilList.Corporate_Info.filter(cust => cust.Corporate_Approval_Status_Id === option);
      setTableList(newList);
    }
  }

  function handleRowClick(corp) {
    navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/details/${corp?.Corporate_Id}`);
  }

  const paginationOptions = {
    pageSize: 10,
    showSizeChanger: true,
    total: tableList.length,
  };

  // ====================== EXPORT CSV ======================
  const exportToCSV = () => {
    if (!tableList?.length) {
      logicContext.showToast('No data to export', 'warning');
      return;
    }

    const headers = columns.map(col => col.title);
    const keys = columns.map(col => col.dataIndex);

    const dataForExport = tableList.map(row => {
      const obj = {};
      keys.forEach((key, i) => {
        obj[headers[i]] = row[key] ?? '';
      });
      return obj;
    });

    const csv = Papa.unparse(dataForExport, { header: true });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `corporate_approval_${moment().format('YYYY-MM-DD_HHmmss')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    logicContext.showToast('✅ CSV exported successfully!', 'success');
  };
  // =======================================================

  return (
    <>
      <section className="w-full bg-white">
        <section className="flex flex-col gap-4 sm:pt-5 xl:flex-row xl:items-center xl:justify-between">
          {/* Left side: Refresh, Total & Filter */}
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center xl:w-auto">
            <div className="flex items-center gap-3">
              <Tooltip title="Reload">
                <button
                  type="button"
                  onClick={getCorpListList}
                  className="shrink-0 rounded-xl border border-gray-200 p-2.5 text-gray-500 transition-all hover:border-blue-200 hover:bg-gray-50 hover:text-blue-600 active:scale-95"
                >
                  <IoMdRefresh className="h-5 w-5" />
                </button>
              </Tooltip>

              <Tooltip title="Total">
                <div className="flex items-center rounded-lg border border-blue-100 bg-blue-50/50 px-4 py-2.5 text-sm font-semibold text-blue-700 shadow-sm transition-colors hover:bg-blue-50">
                  <span className="mr-1">Total:</span>
                  <span>{tableList?.length}</span>
                </div>
              </Tooltip>
            </div>

            <div className="w-full flex-1 sm:w-auto sm:min-w-[200px]">
              <CoApproveFilter approve_status={getRecoilList.approval_type} onStatusFilterSelect={onStatusFilterSelect} />
            </div>
          </div>

          {/* Right side: Actions */}
          <div className="flex w-full items-center gap-3 xl:w-auto">
            <button
              onClick={exportToCSV}
              className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-4 py-2.5 font-[Poppins] text-xs font-semibold text-gray-700 transition-all hover:border-gray-300 hover:bg-gray-50 active:bg-gray-100 sm:flex-none xl:py-2.5"
            >
              <IoCloudDownloadOutline className="h-[18px] w-[18px] shrink-0 text-gray-500" />
              EXPORT CSV
            </button>
          </div>
        </section>

        <section className="mt-5">
          <GeneralCustomTable
            tableHeadList={columns}
            tableDataList={tableList}
            allowCheckBox={false}
            paginationOptions={paginationOptions}
            rowKey={record => record.Corporate_Id} // ← Fixed: was Item_Group_Id
            handleRowClick={handleRowClick}
          />
        </section>
      </section>
    </>
  );
}

export default CorporateApprovalPage;
